export type PendenciasListFilters = {
  statusBusca: number;
  filtroEstagiario?: number;
  filtroResponsavel?: string;
  orderBy?: string;
  orderDir?: 'ASC' | 'DESC';
  page?: number;
  pageSize?: number;
};

export const PENDENCIAS_ORDER_PADRAO = {
  orderBy: 'referencia',
  orderDir: 'DESC' as const,
};

export const PENDENCIAS_PAGE_SIZE_PADRAO = 12;

export const PENDENCIAS_FILTER_PARAM = {
  status: 'status',
  estagiario: 'estagiario',
  responsavel: 'responsavel',
  orderBy: 'orderBy',
  orderDir: 'orderDir',
  page: 'page',
  pageSize: 'pageSize',
} as const;

function parsePositiveInt(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function normalizeOrderDir(value: unknown, orderBy: string): 'ASC' | 'DESC' {
  if (value === 'ASC' || value === 'DESC') return value;
  return orderBy === PENDENCIAS_ORDER_PADRAO.orderBy ? 'DESC' : 'ASC';
}

function normalizeFilters(filters: PendenciasListFilters): PendenciasListFilters {
  const statusBusca = Number.isFinite(filters.statusBusca) ? filters.statusBusca : 0;
  const filtroEstagiario = filters.filtroEstagiario && filters.filtroEstagiario > 0
    ? filters.filtroEstagiario
    : undefined;
  const filtroResponsavel =
    filters.filtroResponsavel && filters.filtroResponsavel !== '0'
      ? filters.filtroResponsavel
      : undefined;
  const orderBy = filters.orderBy?.trim() || PENDENCIAS_ORDER_PADRAO.orderBy;
  const orderDir = normalizeOrderDir(filters.orderDir, orderBy);
  const page = filters.page && filters.page > 0 ? filters.page : undefined;
  const pageSize = filters.pageSize && filters.pageSize > 0 ? filters.pageSize : undefined;
  return { statusBusca, filtroEstagiario, filtroResponsavel, orderBy, orderDir, page, pageSize };
}

export function pendenciasFiltersAreMeaningful(filters: PendenciasListFilters): boolean {
  const n = normalizeFilters(filters);
  return n.statusBusca !== 0
    || n.filtroEstagiario != null
    || n.filtroResponsavel != null
    || n.orderBy !== PENDENCIAS_ORDER_PADRAO.orderBy
    || n.orderDir !== PENDENCIAS_ORDER_PADRAO.orderDir
    || (n.pageSize != null && n.pageSize !== PENDENCIAS_PAGE_SIZE_PADRAO)
    || (n.page != null && n.page !== 1);
}

export function searchParamsHavePendenciasFilters(searchParams: URLSearchParams): boolean {
  return (
    searchParams.has(PENDENCIAS_FILTER_PARAM.status) ||
    searchParams.has(PENDENCIAS_FILTER_PARAM.estagiario) ||
    searchParams.has(PENDENCIAS_FILTER_PARAM.responsavel)
  );
}

export function readPendenciasFiltersFromSearchParams(
  searchParams: URLSearchParams,
): PendenciasListFilters {
  const statusRaw = searchParams.get(PENDENCIAS_FILTER_PARAM.status);
  const statusBusca = statusRaw == null || statusRaw === '' ? 0 : Number(statusRaw);
  const orderDirRaw = searchParams.get(PENDENCIAS_FILTER_PARAM.orderDir);
  return normalizeFilters({
    statusBusca: Number.isFinite(statusBusca) ? statusBusca : 0,
    filtroEstagiario: parsePositiveInt(searchParams.get(PENDENCIAS_FILTER_PARAM.estagiario)),
    filtroResponsavel: searchParams.get(PENDENCIAS_FILTER_PARAM.responsavel) ?? undefined,
    orderBy: searchParams.get(PENDENCIAS_FILTER_PARAM.orderBy) ?? undefined,
    orderDir: orderDirRaw === 'ASC' || orderDirRaw === 'DESC' ? orderDirRaw : undefined,
    page: parsePositiveInt(searchParams.get(PENDENCIAS_FILTER_PARAM.page)),
    pageSize: parsePositiveInt(searchParams.get(PENDENCIAS_FILTER_PARAM.pageSize)),
  });
}

export function applyPendenciasFiltersToSearchParams(
  prev: URLSearchParams,
  filters: PendenciasListFilters,
): URLSearchParams {
  const next = new URLSearchParams(prev);
  const n = normalizeFilters(filters);

  if (n.statusBusca !== 0) next.set(PENDENCIAS_FILTER_PARAM.status, String(n.statusBusca));
  else next.delete(PENDENCIAS_FILTER_PARAM.status);

  if (n.filtroEstagiario != null) {
    next.set(PENDENCIAS_FILTER_PARAM.estagiario, String(n.filtroEstagiario));
  } else {
    next.delete(PENDENCIAS_FILTER_PARAM.estagiario);
  }

  if (n.filtroResponsavel) next.set(PENDENCIAS_FILTER_PARAM.responsavel, n.filtroResponsavel);
  else next.delete(PENDENCIAS_FILTER_PARAM.responsavel);

  if (n.orderBy) next.set(PENDENCIAS_FILTER_PARAM.orderBy, n.orderBy);
  else next.delete(PENDENCIAS_FILTER_PARAM.orderBy);

  if (n.orderDir) next.set(PENDENCIAS_FILTER_PARAM.orderDir, n.orderDir);
  else next.delete(PENDENCIAS_FILTER_PARAM.orderDir);

  if (n.pageSize && n.pageSize !== PENDENCIAS_PAGE_SIZE_PADRAO) {
    next.set(PENDENCIAS_FILTER_PARAM.pageSize, String(n.pageSize));
  } else {
    next.delete(PENDENCIAS_FILTER_PARAM.pageSize);
  }

  return next;
}

/** Lê só a query da navegação atual. Entrada sem esses parâmetros começa do padrão. */
export function resolvePendenciasFiltersOnLoad(
  searchParams: URLSearchParams,
): PendenciasListFilters {
  return readPendenciasFiltersFromSearchParams(searchParams);
}
